import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Grid, IconButton, InputBase, ListItemText, MenuItem, Select, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import { formatDisplayName } from '../../../../../../../../utils/formatters'
import { MAX_ROWS } from '../../../../../../../organisms/QueryBlueprint/hooks/useExhaustiveBlueprintData'
import Alert from '../../../../../../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../../../../../../constants'
import ColumnConfigEditor, { DATA_TYPE_TO_SORT_AS } from './ColumnConfigEditor'
import StyleVariantPicker from './StyleVariantPicker'
import { useGroupBySelectStyles } from './groupBySelectStyles'
import LocalStorageAndFeatureToggles from './LocalStorageAndFeatureToggles'
import ExportSettingsEditor from './ExportSettingsEditor'
import DefaultSortEditor from './DefaultSortEditor'
import FooterMappingEditor from './FooterMappingEditor'

const AGGREGATE_FN_OPTIONS = [
  { label: 'Sum', value: 'sum' },
  { label: 'Average', value: 'avg' },
  { label: 'Min', value: 'min' },
  { label: 'Max', value: 'max' },
  { label: 'Count', value: 'count' },
  { label: 'First', value: 'first' },
  { label: 'Last', value: 'last' }
]

const AGGREGATION_MODE_OPTIONS = [
  { label: 'None (labels only)', value: 'none' },
  { label: 'Client-side aggregation', value: 'client' },
  { label: 'Server-provided depths', value: 'server' }
]

function HierarchicalTableConfigEditor ({ config, allPurposes, onUpdate }) {
  const groupByClasses = useGroupBySelectStyles()
  const [localConfig, setLocalConfig] = useState(config)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const footerPurposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'None', value: '__none__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const selectedPurposeColumns = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) return []
    return allPurposes[localConfig.purposeCode].columns || []
  }, [localConfig.purposeCode, allPurposes])

  const availableColumnsWithLabel = useMemo(() => {
    const labelCol = localConfig.hierarchy?.labelColumn || '_hierarchyLabel'
    const syntheticCol = { name: labelCol, dataType: 'string', description: 'Synthetic hierarchy label' }
    return [syntheticCol, ...selectedPurposeColumns]
  }, [selectedPurposeColumns, localConfig.hierarchy?.labelColumn])

  const columnSelectOptions = useMemo(() => {
    return selectedPurposeColumns.map(col => ({
      label: `${formatDisplayName(col.name)} (${col.name})`,
      value: col.name
    }))
  }, [selectedPurposeColumns])

  const footerPurposeColumns = useMemo(() => {
    if (!localConfig.footerPurposeCode || !allPurposes[localConfig.footerPurposeCode]) return []
    return allPurposes[localConfig.footerPurposeCode].columns || []
  }, [localConfig.footerPurposeCode, allPurposes])

  // --- Purpose selection ---
  const handlePurposeChange = useCallback((purposeCode) => {
    const normalized = purposeCode === '__placeholder__' ? null : purposeCode
    const purpose = allPurposes[normalized]
    if (purpose && purpose.columns) {
      const prefillColumns = purpose.columns.map(col => ({
        name: col.name,
        displayName: formatDisplayName(col.name),
        description: col.description || null,
        formatString: null,
        align: ['integer', 'decimal'].includes(col.dataType) ? 'right' : 'left',
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        hidden: col.hidden || false,
        exclude: false,
        width: 150,
        sortAs: DATA_TYPE_TO_SORT_AS[col.dataType] || null
      }))
      setLocalConfig(prev => {
        const labelCol = prev.hierarchy?.labelColumn || '_hierarchyLabel'
        const labelEntry = {
          name: labelCol,
          displayName: 'Name',
          description: null,
          formatString: null,
          align: 'left',
          sortable: true,
          filterable: false,
          hidden: false,
          exclude: false,
          width: 300,
          sortAs: null
        }
        return { ...prev, purposeCode: normalized, columns: [labelEntry, ...prefillColumns] }
      })
    } else {
      setLocalConfig(prev => ({ ...prev, purposeCode: normalized, columns: [] }))
    }
  }, [allPurposes])

  // --- Hierarchy levels ---
  const levels = localConfig.hierarchy?.levels || []

  const handleAddLevel = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: [...(prev.hierarchy?.levels || []), { groupBy: [], labelAccessor: '' }]
      }
    }))
  }, [])

  const handleRemoveLevel = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).filter((_, i) => i !== index)
      }
    }))
  }, [])

  const handleUpdateLevel = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).map((lvl, i) =>
          i === index ? { ...lvl, [field]: value } : lvl
        )
      }
    }))
  }, [])

  const handleExpandDepthChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        defaultExpandDepth: Number(value) || 0
      }
    }))
  }, [])

  // --- Aggregation ---
  const aggregation = localConfig.hierarchy?.aggregation || { mode: 'none' }

  const handleAggregationModeChange = useCallback((mode) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          mode,
          columnAggregates: mode === 'client' ? (prev.hierarchy?.aggregation?.columnAggregates || []) : [],
          depthColumn: mode === 'server' ? (prev.hierarchy?.aggregation?.depthColumn || null) : null
        }
      }
    }))
  }, [])

  const handleAddAggregate = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: [
            ...(prev.hierarchy?.aggregation?.columnAggregates || []),
            { column: '', fn: 'sum' }
          ]
        }
      }
    }))
  }, [])

  const handleRemoveAggregate = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: (prev.hierarchy?.aggregation?.columnAggregates || []).filter((_, i) => i !== index)
        }
      }
    }))
  }, [])

  const handleUpdateAggregate = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: (prev.hierarchy?.aggregation?.columnAggregates || []).map((agg, i) =>
            i === index ? { ...agg, [field]: value } : agg
          )
        }
      }
    }))
  }, [])

  const handleDepthColumnChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          depthColumn: value || null
        }
      }
    }))
  }, [])

  // --- Column management ---
  const handleAddColumn = useCallback((column) => {
    setLocalConfig(prev => ({ ...prev, columns: [...prev.columns, column] }))
  }, [])

  const handleRemoveColumn = useCallback((index) => {
    setLocalConfig(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }))
  }, [])

  const handleUpdateColumn = useCallback((index, updated) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? updated : col)
    }))
  }, [])

  const handleColumnsChange = useCallback((newColumns) => {
    setLocalConfig(prev => ({ ...prev, columns: newColumns }))
  }, [])

  // --- Default sort ---
  const handleAddSortField = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: [...(prev.defaultSort || []), { field: '', dir: 'asc' }]
    }))
  }, [])

  const handleRemoveSortField = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateSortField = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.map((sort, i) =>
        i === index ? { ...sort, [field]: value } : sort
      )
    }))
  }, [])

  const sortFieldOptions = useMemo(() => {
    return (localConfig.columns || []).map(col => ({
      label: col.displayName || col.name,
      value: col.name
    }))
  }, [localConfig.columns])

  // --- Footer ---
  const handleFooterPurposeChange = useCallback((footerPurposeCode) => {
    setLocalConfig(prev => ({
      ...prev,
      footerPurposeCode: footerPurposeCode === '__none__' ? null : footerPurposeCode,
      footerColumnMapping: []
    }))
  }, [])

  const getFooterColumnForMain = useCallback((mainColumnName) => {
    const mapping = (localConfig.footerColumnMapping || []).find(m => m.mainColumn === mainColumnName)
    return mapping?.footerColumn || '__none__'
  }, [localConfig.footerColumnMapping])

  const handleFooterMappingChange = useCallback((mainColumnName, footerColumnName) => {
    setLocalConfig(prev => {
      let newMappings = (prev.footerColumnMapping || []).filter(m => m.mainColumn !== mainColumnName)
      if (footerColumnName && footerColumnName !== '__none__') {
        newMappings = [...newMappings, { mainColumn: mainColumnName, footerColumn: footerColumnName }]
      }
      return { ...prev, footerColumnMapping: newMappings }
    })
  }, [])

  const handleClearFooterMappings = useCallback(() => {
    setLocalConfig(prev => ({ ...prev, footerColumnMapping: [] }))
  }, [])

  const handleAutoConfigureFooterMapping = useCallback(() => {
    const footerColumnNames = new Set(footerPurposeColumns.map(c => c.name))
    const newMappings = (localConfig.columns || [])
      .filter(col => footerColumnNames.has(col.name))
      .map(col => ({ mainColumn: col.name, footerColumn: col.name }))
    setLocalConfig(prev => ({ ...prev, footerColumnMapping: newMappings }))
  }, [localConfig.columns, footerPurposeColumns])

  const footerColumnSelectOptions = useMemo(() => {
    return [
      { label: '(None)', value: '__none__' },
      ...footerPurposeColumns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [footerPurposeColumns])

  const mappedColumnCount = useMemo(() => {
    return (localConfig.footerColumnMapping || []).length
  }, [localConfig.footerColumnMapping])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert variant={ALERT_VARIANT.outlined} severity={ALERT_SEVERITY.info}>
          Hierarchical Table loads all data client-side (up to {MAX_ROWS.toLocaleString()} rows).
          For datasets expected to exceed this limit, consider using <strong>Hierarchical Grid</strong> which supports server-side pagination.
        </Alert>
      </Grid>

      {/* Data Purpose Selection */}
      <Grid item xs={12}>
        <SydLabel required label='Data Configuration Purpose' helperText='Select which data purpose to display'>
          <SydSelect
            options={purposeOptions}
            value={localConfig.purposeCode || '__placeholder__'}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Hierarchy Levels */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Hierarchy Levels</h4>
          <SydButton
            size='sm'
            icon='add'
            onClick={handleAddLevel}
            disabled={!localConfig.purposeCode}
          >
            Add Level
          </SydButton>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '4px' }}>
          Define grouping levels from broadest to narrowest. Rows below the last level become leaf nodes.
        </p>
        {levels.length > 0
          ? (
            <Table className='opal-presentation-table zebra-rows' size='small' style={{ marginBottom: '12px' }}>
              <TableHead className='opt-thead'>
                <TableRow className='opt-tr'>
                  <TableCell className='opt-th' style={{ width: '40px' }}>#</TableCell>
                  <TableCell className='opt-th'>Group By Columns</TableCell>
                  <TableCell className='opt-th'>Label Column</TableCell>
                  <TableCell className='opt-th' style={{ width: '48px' }} />
                </TableRow>
              </TableHead>
              <TableBody className='opt-tbody'>
                {levels.map((level, index) => (
                  <TableRow className='opt-tr' key={index}>
                    <TableCell className='opt-td'>{index + 1}</TableCell>
                    <TableCell className='opt-td'>
                      <Select
                        multiple
                        value={level.groupBy || []}
                        onChange={(e) => handleUpdateLevel(index, 'groupBy', e.target.value)}
                        renderValue={(selected) => selected.join(', ')}
                        displayEmpty
                        fullWidth
                        classes={{ select: groupByClasses.select }}
                        input={<InputBase classes={{ root: groupByClasses.inputRoot, input: groupByClasses.input }} />}
                        MenuProps={{
                          getContentAnchorEl: null,
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          classes: { paper: groupByClasses.menuPaper, list: groupByClasses.menuList }
                        }}
                      >
                        {columnSelectOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value} classes={{ root: groupByClasses.menuItem }}>
                            <Checkbox checked={(level.groupBy || []).includes(opt.value)} size='small' />
                            <ListItemText primary={formatDisplayName(opt.value)} secondary={opt.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell className='opt-td'>
                      <SydSelect
                        options={[{ label: 'Select...', value: '__placeholder__' }, ...columnSelectOptions]}
                        value={level.labelAccessor || '__placeholder__'}
                        onChange={(e) => handleUpdateLevel(index, 'labelAccessor', e.target.value === '__placeholder__' ? '' : e.target.value)}
                        size='sm'
                      />
                    </TableCell>
                    <TableCell className='opt-td'>
                      <IconButton size='small' onClick={() => handleRemoveLevel(index)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className='opt-tr'>
                  <TableCell className='opt-td' style={{ color: 'var(--text-color-subtle)' }}>{levels.length + 1}</TableCell>
                  <TableCell className='opt-td' colSpan={2} style={{ color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>
                    Detail rows (leaf level)
                  </TableCell>
                  <TableCell className='opt-td' />
                </TableRow>
              </TableBody>
            </Table>
          )
          : (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: '0 0 12px' }}>
              No hierarchy levels configured. Add at least one level.
            </p>
          )}
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <SydLabel label='Default Expand Depth' helperText='Levels auto-expanded on load (0 = all collapsed)'>
          <SydInput
            type='number'
            value={localConfig.hierarchy?.defaultExpandDepth ?? 1}
            onChange={(e) => handleExpandDepthChange(e.target.value)}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <SydLabel label='Label Column Name' helperText='Synthetic field name for the display label'>
          <SydInput
            value={localConfig.hierarchy?.labelColumn ?? '_hierarchyLabel'}
            onChange={(e) => {
              const newLabel = e.target.value || '_hierarchyLabel'
              setLocalConfig(prev => {
                const prevLabel = prev.hierarchy?.labelColumn || '_hierarchyLabel'
                if (prevLabel === newLabel) {
                  return { ...prev, hierarchy: { ...prev.hierarchy, labelColumn: newLabel } }
                }
                return {
                  ...prev,
                  hierarchy: { ...prev.hierarchy, labelColumn: newLabel },
                  columns: (prev.columns || []).map(col =>
                    col.name === prevLabel ? { ...col, name: newLabel } : col
                  ),
                  defaultSort: (prev.defaultSort || []).map(s =>
                    s.field === prevLabel ? { ...s, field: newLabel } : s
                  )
                }
              })
            }}
            placeholder='_hierarchyLabel'
          />
        </SydLabel>
      </Grid>
      <Grid item xs={12} md={5} lg={4}>
        <SydLabel label='Leaf Label Accessor' helperText='Source column for leaf row labels'>
          <SydSelect
            options={[{ label: '(None)', value: '__none__' }, ...columnSelectOptions]}
            value={localConfig.hierarchy?.leafLabelAccessor || '__none__'}
            onChange={(e) => setLocalConfig(prev => ({
              ...prev,
              hierarchy: { ...prev.hierarchy, leafLabelAccessor: e.target.value === '__none__' ? undefined : e.target.value }
            }))}
          />
        </SydLabel>
      </Grid>

      {/* Aggregation */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Aggregation</h4>
        </div>
        <SydLabel label='Mode' helperText='How parent node values are computed'>
          <SydSelect
            options={AGGREGATION_MODE_OPTIONS}
            value={aggregation.mode || 'none'}
            onChange={(e) => handleAggregationModeChange(e.target.value)}
          />
        </SydLabel>

        {aggregation.mode === 'client'
          ? (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Column Aggregates</span>
                <SydButton size='sm' icon='add' onClick={handleAddAggregate}>
                  Add
                </SydButton>
              </div>
              {(aggregation.columnAggregates || []).map((agg, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <SydSelect
                      options={[{ label: 'Select column...', value: '__placeholder__' }, ...columnSelectOptions]}
                      value={agg.column || '__placeholder__'}
                      onChange={(e) => handleUpdateAggregate(index, 'column', e.target.value === '__placeholder__' ? '' : e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <SydSelect
                      options={AGGREGATE_FN_OPTIONS}
                      value={agg.fn || 'sum'}
                      onChange={(e) => handleUpdateAggregate(index, 'fn', e.target.value)}
                    />
                  </div>
                  <IconButton size='small' onClick={() => handleRemoveAggregate(index)}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </div>
              ))}
            </div>
          )
          : null}

        {aggregation.mode === 'server'
          ? (
            <div style={{ marginTop: '12px' }}>
              <SydLabel label='Depth Column' helperText='Column in query results that indicates the row depth (0, 1, 2...)'>
                <SydSelect
                  options={[{ label: 'Select...', value: '' }, ...columnSelectOptions]}
                  value={aggregation.depthColumn || ''}
                  onChange={(e) => handleDepthColumnChange(e.target.value)}
                />
              </SydLabel>
            </div>
          )
          : null}
      </Grid>

      {/* Column Configuration */}
      <Grid item xs={12}>
        <ColumnConfigEditor
          columns={localConfig.columns || []}
          availableColumns={availableColumnsWithLabel}
          onAddColumn={handleAddColumn}
          onRemoveColumn={handleRemoveColumn}
          onUpdateColumn={handleUpdateColumn}
          onColumnsChange={handleColumnsChange}
          showSortAs
        />
      </Grid>

      {/* Footer Purpose */}
      <Grid item xs={12}>
        <SydLabel label='Footer Data Purpose' helperText='Optional purpose for footer/summary data'>
          <SydSelect
            options={footerPurposeOptions}
            value={localConfig.footerPurposeCode || '__none__'}
            onChange={(e) => handleFooterPurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Footer Column Mapping */}
      {localConfig.footerPurposeCode
        ? (
          <FooterMappingEditor
            localConfig={localConfig}
            getFooterColumnForMain={getFooterColumnForMain}
            onFooterMappingChange={handleFooterMappingChange}
            footerColumnSelectOptions={footerColumnSelectOptions}
            mappedColumnCount={mappedColumnCount}
            onAutoConfigure={handleAutoConfigureFooterMapping}
            onClear={handleClearFooterMappings}
          />
        )
        : null}

      {/* Default Sort */}
      <DefaultSortEditor
        localConfig={localConfig}
        setLocalConfig={setLocalConfig}
        sortFieldOptions={sortFieldOptions}
        onAddSort={handleAddSortField}
        onRemoveSort={handleRemoveSortField}
        onUpdateSort={handleUpdateSortField}
      />

      <LocalStorageAndFeatureToggles localConfig={localConfig} setLocalConfig={setLocalConfig} />

      <ExportSettingsEditor localConfig={localConfig} setLocalConfig={setLocalConfig} />

      <StyleVariantPicker
        presentationType='Hierarchical Table'
        value={localConfig.styleVariant}
        onChange={(v) => setLocalConfig(prev => ({ ...prev, styleVariant: v, styleVariantParams: {} }))}
        variantParams={localConfig.styleVariantParams}
        onParamsChange={(p) => setLocalConfig(prev => ({ ...prev, styleVariantParams: p }))}
      />
    </Grid>
  )
}

HierarchicalTableConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default HierarchicalTableConfigEditor
